/**
 * Auth bootstrap: Firebase session, user payload, preferences, and RBAC cache.
 */
import * as React from 'react';
import { onAuthStateChanged, signOut, User as FirebaseUser } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { useAuthStore } from '@/stores/authStore';
import { usePreferencesStore } from '@/stores/preferencesStore';
import { useInterfaceElementsStore } from '@/stores/interfaceElementsStore';
import { fetchUserData } from '@/services/auth.service';
import { getUserPreferences } from '@/services/preferences.service';
import { getInterfaceElements } from '@/services/interface-elements.service';
import { fetchRoles } from '@/services/roles.service';
import { getInitialInterfacesToLoad } from '@/utils/dashboard-ie-utils';
import {
  canRestoreStaffSession,
  confirmFirebaseSignedOut,
  waitForStoreHydration,
} from '@/utils/auth-session-utils';
import { useRoleCatalogStore } from '@/stores/roleCatalogStore';
import type { InterfaceAccess } from '@/types/permissions.types';
import type { InterfaceElement } from '@/types/interface-elements.types';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/forms/Button';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { toast } from '@/components/ui/toast';
import {
  getErrorMessage,
  isTutorBlockedError,
  isPendingActivationError,
} from '@/utils/errors-utils';
import { getPushToken, clearPushToken } from '@/hooks/usePushNotifications';
import { notificationsApi } from '@/services/notifications.service';

/** Backoff delays for transient errors (network / 5xx). Retry instead of signing out. */
const BOOTSTRAP_RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 30_000];
/** How many transient bootstrap failures to retry before giving up. */
const MAX_BOOTSTRAP_RETRIES = BOOTSTRAP_RETRY_DELAYS_MS.length;

/** Staff app tutoring mode (hybrid roles can switch). */
type AppMode = 'in_person' | 'remote';
/** Assigned user mode (`hybrid` can switch); null/undefined while bootstrap is in flight. */
type UserMode = AppMode | 'hybrid' | null | undefined;
/** Interface-elements store snapshot for bootstrap helpers. */
type InterfaceStore = ReturnType<typeof useInterfaceElementsStore.getState>;

/** Distinguishes real auth errors from those that should be retried. */
const isAuthError = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  const code = (error as { code?: string })?.code ?? '';
  return status === 401 || status === 403 || code.startsWith('auth/');
};

/** Best-effort FCM token unregister so the next login does not inherit this device. */
const unregisterPushToken = async (): Promise<void> => {
  const pushToken = getPushToken();
  if (!pushToken) return;
  try {
    await notificationsApi.unregisterPushToken(pushToken);
    clearPushToken();
  } catch (error) {
    console.error('[AuthProvider] Failed to unregister push token:', error);
  }
};

/** Flag + Firebase sign-out so `onAuthStateChanged` does not treat this as a session drop. */
const forceIntentionalLogout = async (logout: () => void): Promise<void> => {
  localStorage.setItem('intentional_logout', 'true');
  await unregisterPushToken();
  await signOut(auth);
  logout();
};

/** Wait until session + preferences + RBAC caches are rehydrated so a refresh can skip login. */
const waitForAuthCaches = async (): Promise<void> => {
  await Promise.all([
    waitForStoreHydration(useAuthStore),
    waitForStoreHydration(usePreferencesStore),
    waitForStoreHydration(useInterfaceElementsStore),
  ]);
};

/** Restore prefs, role catalog, and missing IE from persist — no GET /users/login. */
const restorePersistedSession = async (firebaseUser: FirebaseUser): Promise<boolean> => {
  const authStore = useAuthStore.getState();
  if (!canRestoreStaffSession(authStore.user, authStore.roles, firebaseUser.uid)) {
    return false;
  }

  await firebaseUser.getIdToken();
  if (authStore.user && !authStore.isAuthenticated) {
    authStore.setAuthData(authStore.user, authStore.roles, authStore.interfaceAccess);
  }

  const storedPrefs = usePreferencesStore.getState();
  await Promise.all([
    useRoleCatalogStore.getState().isLoaded ? Promise.resolve() : fetchRoles(),
    storedPrefs.isLoaded && storedPrefs.preferences
      ? Promise.resolve()
      : getUserPreferences().then((prefs) => {
          usePreferencesStore.getState().setPreferences(prefs);
        }),
  ]);

  const prefs = usePreferencesStore.getState().preferences;
  const savedAppMode = prefs?.interfaceConfigs?.['global']?.['appMode'] as AppMode | undefined;
  await preloadMissingInterfaces(
    useInterfaceElementsStore.getState(),
    prefs?.interfaceConfigs?.['dashboard'],
    authStore.user?.mode,
    resolveInitialMode(authStore.user?.mode, savedAppMode),
  );
  return true;
};

/** Hybrid users keep the last saved mode; single-mode users are pinned. */
const resolveInitialMode = (userMode: UserMode, savedAppMode?: AppMode): AppMode | undefined => {
  if (userMode === 'hybrid') return savedAppMode ?? 'in_person';
  if (userMode === 'in_person' || userMode === 'remote') return userMode;
  return undefined;
};

/** Fetch RBAC layouts that the dashboard config expects but the persist cache is missing. */
const preloadMissingInterfaces = async (
  interfaceStore: InterfaceStore,
  dashboardConfig: Record<string, unknown> | undefined,
  userMode: UserMode,
  initialMode: AppMode | undefined,
): Promise<void> => {
  const expectedInterfaces = getInitialInterfacesToLoad(dashboardConfig, userMode);
  const missingInterfaces = expectedInterfaces.filter(
    (name) => !interfaceStore.isInterfaceLoaded(name) && !interfaceStore.isInterfaceLoading(name),
  );
  if (missingInterfaces.length === 0) return;

  missingInterfaces.forEach((name) => interfaceStore.startLoading(name));
  const results = await Promise.all(
    missingInterfaces.map((name) => getInterfaceElements(name, initialMode)),
  );
  const loadedData: Record<string, InterfaceElement[]> = {};
  missingInterfaces.forEach((name, i) => {
    loadedData[name] = results[i];
  });
  interfaceStore.setInterfaceElements(loadedData);
};

/** Store setters + retry refs shared by bootstrap error handlers. */
interface BootstrapErrorHandlers {
  logout: () => void;
  setBootstrapping: (value: boolean) => void;
  setBootstrapFailed: (value: boolean) => void;
  setIsChecking: (value: boolean) => void;
  retryCountRef: { current: number };
  retryTimerRef: { current: ReturnType<typeof setTimeout> | undefined };
}

/** Schedule a backoff retry; after max attempts, show the reconnect screen. */
const handleTransientBootstrapFailure = (
  error: unknown,
  firebaseUser: FirebaseUser,
  runBootstrap: (user: FirebaseUser) => Promise<void>,
  handlers: BootstrapErrorHandlers,
): void => {
  console.error('Bootstrap failed (transient), retrying:', error);
  if (handlers.retryCountRef.current >= MAX_BOOTSTRAP_RETRIES) {
    console.error('Bootstrap failed after max retries, giving up.');
    handlers.setBootstrapping(false);
    handlers.setBootstrapFailed(true);
    handlers.setIsChecking(false);
    return;
  }
  const delayIndex = Math.min(handlers.retryCountRef.current, BOOTSTRAP_RETRY_DELAYS_MS.length - 1);
  const delay = BOOTSTRAP_RETRY_DELAYS_MS[delayIndex];
  handlers.retryCountRef.current += 1;
  toast.error(getErrorMessage('unstableConnection'));
  handlers.retryTimerRef.current = setTimeout(() => void runBootstrap(firebaseUser), delay);
};

/** Auth/403/pending-activation → logout; everything else → transient retry. */
const handleBootstrapError = async (
  error: unknown,
  firebaseUser: FirebaseUser,
  runBootstrap: (user: FirebaseUser) => Promise<void>,
  handlers: BootstrapErrorHandlers,
): Promise<void> => {
  if (isTutorBlockedError(error)) {
    console.error('Staff web access denied (403): role group not allowed');
    await forceIntentionalLogout(handlers.logout);
    handlers.setBootstrapping(false);
    handlers.setIsChecking(false);
    return;
  }

  if (isPendingActivationError(error)) {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('pending_activation', 'true');
      sessionStorage.setItem('pending_activation_message', getErrorMessage(error));
    }
    toast.error(getErrorMessage(error));
    await forceIntentionalLogout(handlers.logout);
    handlers.setBootstrapping(false);
    handlers.setIsChecking(false);
    return;
  }

  if (isAuthError(error)) {
    console.error('Auth restoration failed (auth error):', error);
    await forceIntentionalLogout(handlers.logout);
    handlers.setBootstrapping(false);
    handlers.setIsChecking(false);
    return;
  }

  handleTransientBootstrapFailure(error, firebaseUser, runBootstrap, handlers);
};

/** Props for {@link AuthProvider}. */
interface AuthProviderProps {
  children: React.ReactNode;
}

/** Blocks the tree on a spinner until Firebase session + user payload are restored. */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isChecking, setIsChecking] = React.useState(true);
  const [bootstrapFailed, setBootstrapFailed] = React.useState(false);
  const { setAuthData, logout, setBootstrapping } = useAuthStore();
  const { setPreferences } = usePreferencesStore();

  // Retry counter/timer stay stable across renders (not reset by StrictMode remount).
  const retryCountRef = React.useRef(0);
  const retryTimerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastFirebaseUserRef = React.useRef<FirebaseUser | null>(null);
  const runBootstrapRef = React.useRef<((user: FirebaseUser) => Promise<void>) | null>(null);

  React.useEffect(() => {
    /**
     * Load user data after onAuthStateChanged provides a FirebaseUser.
     * Transient errors (network / 5xx) schedule a backoff retry instead of signing out.
     * Reveals the app (setIsChecking(false)) only after bootstrap or a real auth error.
     */
    const runBootstrap = async (firebaseUser: FirebaseUser): Promise<void> => {
      lastFirebaseUserRef.current = firebaseUser;
      runBootstrapRef.current = runBootstrap;
      const errorHandlers: BootstrapErrorHandlers = {
        logout,
        setBootstrapping,
        setBootstrapFailed,
        setIsChecking,
        retryCountRef,
        retryTimerRef,
      };
      try {
        setBootstrapping(true);
        await waitForAuthCaches();

        if (await restorePersistedSession(firebaseUser)) {
          setBootstrapping(false);
          setIsChecking(false);
          return;
        }

        // Fresh ID token before the first API calls.
        await firebaseUser.getIdToken();

        const storedPrefs = usePreferencesStore.getState();
        const interfaceStore = useInterfaceElementsStore.getState();

        const [userData, preferences] = await Promise.all([
          fetchUserData(),
          storedPrefs.isLoaded ? Promise.resolve(storedPrefs.preferences!) : getUserPreferences(),
          fetchRoles(),
        ]);

        const roles =
          userData.userRoles
            ?.filter((ur) => ur.role)
            .map((ur) => ({
              id: ur.role!.id,
              code: ur.role!.code,
              label: ur.role!.label,
              permissions: ur.role!.permissions || {},
              roleGroup: ur.role!.roleGroup,
            })) || [];

        if (!storedPrefs.isLoaded) {
          setPreferences(preferences);
        }

        const userMode = userData.mode;
        const savedAppMode = preferences.interfaceConfigs?.['global']?.['appMode'] as
          | AppMode
          | undefined;
        await preloadMissingInterfaces(
          interfaceStore,
          preferences.interfaceConfigs?.['dashboard'],
          userMode,
          resolveInitialMode(userMode, savedAppMode),
        );

        retryCountRef.current = 0;
        setAuthData(
          userData,
          roles,
          (userData.interfaceAccess || {}) as Record<string, InterfaceAccess>,
        );
        setIsChecking(false);
      } catch (error) {
        await handleBootstrapError(error, firebaseUser, runBootstrap, errorHandlers);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Cancel an in-flight retry if auth state fires again (e.g. logout).
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = undefined;
      }
      retryCountRef.current = 0;
      setBootstrapFailed(false);

      if (firebaseUser) {
        await runBootstrap(firebaseUser);
        return;
      }

      const wasIntentionalLogout = localStorage.getItem('intentional_logout') === 'true';
      localStorage.removeItem('intentional_logout');

      if (!wasIntentionalLogout) {
        const signedOut = await confirmFirebaseSignedOut(auth);
        if (!signedOut && auth.currentUser) {
          await runBootstrap(auth.currentUser);
          return;
        }
        if (!signedOut) return;
      }

      const authStore = useAuthStore.getState();
      if (authStore.isAuthenticated || wasIntentionalLogout) {
        logout();
      }

      setBootstrapping(false);
      setIsChecking(false);
    });

    return () => {
      unsubscribe();
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = undefined;
      }
    };
    // Zustand actions have stable references — intentionally no deps.
  }, []);

  /** Reset retry state and trigger a new bootstrap attempt. */
  const handleRetry = (): void => {
    const firebaseUser = lastFirebaseUserRef.current;
    const runBootstrap = runBootstrapRef.current;
    if (!firebaseUser || !runBootstrap) return;
    retryCountRef.current = 0;
    setBootstrapFailed(false);
    setIsChecking(true);
    void runBootstrap(firebaseUser);
  };

  if (bootstrapFailed) {
    return (
      <div className="flex flex-col items-center justify-center min-h-full p-6 text-center bg-background">
        <div className="rounded-full bg-destructive/10 p-4 mb-4">
          <AlertTriangle className="h-8 w-8 text-destructive" />
        </div>
        <h2 className="text-2xl font-bold mb-2">{getErrorMessage('connectFailed')}</h2>
        <p className="text-muted-foreground mb-4 max-w-md">
          {getErrorMessage('serverTemporarilyUnavailable')}
        </p>
        <Button icon={RotateCcw} onClick={handleRetry}>
          Réessayer
        </Button>
      </div>
    );
  }

  if (isChecking) {
    return (
      <div className="flex items-center justify-center min-h-full bg-background">
        <Spinner variant="default" size="xl" label="Vérification de la session..." />
      </div>
    );
  }

  return <>{children}</>;
};
